"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: { sitekey: string; theme?: "light" | "dark" | "auto" }
      ) => string;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

export type TurnstileWidgetHandle = {
  reset: () => void;
  getToken: () => string | undefined;
};

// Cloudflare Turnstile bot-protection widget - explicit-render pattern,
// mirroring the sibling hcl-lift-line-manager app's client-side approach
// (same site-key-from-our-own-API, render-once-script-and-key-are-ready
// design). If no site key is configured (Turnstile:SiteKey unset on the
// API), this renders nothing and the form works exactly as before -
// matches the backend's fail-open verification behaviour.
const TurnstileWidget = forwardRef<TurnstileWidgetHandle>((_props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/turnstile/site-key")
      .then((res) => res.json())
      .then((data) => setSiteKey(data.siteKey ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    let cancelled = false;

    const tryRender = () => {
      if (cancelled) {
        return;
      }

      if (window.turnstile && containerRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          // TravelStory's palette is light/cream (cardBg/pageBg in
          // styles/theme.ts) - Cloudflare doesn't allow custom widget
          // colors, only a light/dark/auto theme choice.
          theme: "light",
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };

    tryRender();

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current) {
        window.turnstile?.reset(widgetIdRef.current);
      }
    },
    getToken: () => {
      return widgetIdRef.current
        ? window.turnstile?.getResponse(widgetIdRef.current)
        : undefined;
    },
  }));

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} style={{ marginBottom: "10px" }} />;
});

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;
